// Sekcja „Profile dzieci” w panelu rodzica: lista z podsumowaniem postępu, dodawanie, zmiana imienia i wyglądu, archiwizacja i zerowanie postępu.
// Wszystko za PIN-em rodzica (klient panelu); dziecko nie ma tu wejścia, więc nie skasuje sobie postępu przypadkiem.
import type { AdminClient, AdminProfile, AdminProfiles } from '../lib/adminApi.ts';
import { formatBytes, profileLine, recordingLine } from '../lib/adminView.ts';
import { PROFILE_NAME_MAX } from '../lib/look.ts';
import { RECORDING_CONSENT_VERSION } from '../lib/recordingConsent.ts';
import type { Look } from '../lib/look.ts';
import { plPlural } from '../lib/plural.ts';
import { avatarEl } from './avatars.ts';
import { button, h } from './dom.ts';
import type { Child } from './dom.ts';
import { createGenderPicker } from './genderPicker.ts';
import { createLookPicker } from './lookPicker.ts';

export function renderProfilesSection(client: AdminClient, now: () => Date = () => new Date()): HTMLElement {
  const box = h('section', { class: 'adm-profiles', 'data-testid': 'profiles-section' });
  const note = h('p', { class: 'muted', role: 'status', 'data-testid': 'profiles-message' });
  const body = h('div', { 'data-testid': 'profiles-body' }, h('p', { class: 'muted' }, 'Wczytuję profile…'));
  let data: AdminProfiles | null = null;
  let editing: string | 'new' | null = null;
  let busy = false;

  const say = (text: string): void => {
    note.textContent = text;
  };

  async function load(flash = ''): Promise<void> {
    const r = await client.profiles();
    if (!r.ok) {
      body.replaceChildren(h('p', { class: 'adm-error', role: 'alert', 'data-testid': 'profiles-error' }, `❌ ${r.message}`), button('Spróbuj ponownie', () => void load(), { class: 'btn ghost', testid: 'profiles-retry' }));
      return;
    }
    data = r.data;
    say(flash);
    paint();
  }

  /** Wykonuje zmianę na serwerze i odświeża listę; błąd (np. imię zajęte) zostaje na ekranie, formularz się nie zamyka. */
  async function act(run: () => Promise<{ ok: true } | { ok: false; message: string }>, done: string, closeForm = true, reloadOnError = false): Promise<void> {
    if (busy) return;
    busy = true;
    say('Zapisuję…');
    try {
      const r = await run();
      if (!r.ok) {
        // zmiana zgody bywa zapisana mimo błędu (lista dla kalibracji nie dała się odświeżyć) — pokazujemy prawdziwy stan, a nie ten sprzed kliknięcia
        if (reloadOnError) await load(`❌ ${r.message}`);
        else say(`❌ ${r.message}`);
        return;
      }
      if (closeForm) editing = null;
      await load(done);
    } finally {
      busy = false;
    }
  }

  function form(existing: AdminProfile | null): HTMLElement {
    const name = h('input', { type: 'text', maxlength: PROFILE_NAME_MAX, autocomplete: 'off', value: existing?.name ?? '', placeholder: 'Imię albo pseudonim', 'aria-label': 'Imię dziecka', 'data-testid': 'profile-name' });
    const gender = createGenderPicker(existing?.gender ?? '');
    const picker = createLookPicker({ avatar: existing?.avatar ?? '', theme: existing?.theme ?? 'neutral' }, () => {}, { name: () => name.value.trim() });
    // PIN dziecka (patch-34): serwer trzyma tylko jego skrót, więc nie da się go podejrzeć — można ustawić nowy albo usunąć. Widać go podczas pisania, żeby rodzic go zapamiętał.
    let clearPin = false;
    const pinInput = h('input', { type: 'text', inputmode: 'numeric', pattern: '[0-9]*', maxlength: 4, autocomplete: 'off', placeholder: existing?.hasPin ? 'Nowy PIN dziecka (4 cyfry)' : 'PIN dziecka (4 cyfry, nieobowiązkowo)', 'aria-label': 'PIN dziecka', 'data-testid': 'profile-pin' });
    const pinNote = h('p', { class: 'muted', 'data-testid': 'profile-pin-note' }, existing?.hasPin ? '🔒 Profil ma PIN. Wpisz nowy, żeby go zmienić (puste pole zostawia obecny).' : 'Profil bez PIN-u. Z PIN-em dziecko podaje go przy każdym wyborze profilu na ekranie „Kto dziś się uczy?”.');
    pinInput.addEventListener('input', () => {
      const digits = (pinInput as HTMLInputElement).value.replace(/\D/g, '').slice(0, 4);
      if (digits !== (pinInput as HTMLInputElement).value) (pinInput as HTMLInputElement).value = digits;
      if (digits !== '') clearPin = false;
    });
    const pinClear = existing?.hasPin
      ? button('Usuń PIN', () => {
          clearPin = true;
          (pinInput as HTMLInputElement).value = '';
          pinNote.textContent = 'PIN zostanie usunięty po kliknięciu „Zapisz”.';
        }, { class: 'btn link danger', testid: 'profile-pin-clear' })
      : null;
    const save = button(existing ? 'Zapisz' : 'Dodaj profil', () => {
      const look: Look = picker.get();
      const nick = name.value.trim();
      if (!nick) {
        say('❌ Wpisz imię albo pseudonim dziecka.');
        return;
      }
      // przy zakładaniu płeć jest wymagana (formy w ćwiczeniach); starszym profilom bez płci wolno jej nie ustawiać — aplikacja używa wtedy formy męskiej
      if (!existing && !gender.get()) {
        say('❌ Wybierz: chłopiec czy dziewczynka (od tego zależą formy w ćwiczeniach).');
        return;
      }
      const kidPin = (pinInput as HTMLInputElement).value.trim();
      if (kidPin !== '' && !/^[0-9]{4}$/.test(kidPin)) {
        say('❌ PIN dziecka to dokładnie 4 cyfry (albo zostaw puste).');
        return;
      }
      const pin: { pin?: string } = clearPin ? { pin: '' } : kidPin !== '' ? { pin: kidPin } : {}; // brak pola = PIN bez zmian
      void act(
        async () => {
          const r = existing ? await client.updateProfile(existing.id, { name: nick, avatar: look.avatar, theme: look.theme, gender: gender.get(), ...pin }) : await client.createProfile({ name: nick, avatar: look.avatar, theme: look.theme, gender: gender.get(), ...pin });
          return r.ok ? { ok: true } : { ok: false, message: r.message };
        },
        existing ? `Zapisano zmiany: ${nick}.${clearPin ? ' PIN usunięty.' : kidPin ? ' PIN ustawiony.' : ''}` : `Dodano profil: ${nick}.${kidPin ? ' PIN ustawiony.' : ''}`,
      );
    }, { class: 'btn primary', testid: 'profile-save' });
    const cancel = button('Anuluj', () => {
      editing = null;
      say('');
      paint();
    }, { class: 'btn link', testid: 'profile-cancel' });
    return h('div', { class: 'card adm-form', 'data-testid': 'profile-form' }, name, gender.el, picker.el, h('div', { class: 'adm-pin' }, pinInput, pinClear, pinNote), h('div', { class: 'row' }, save, cancel));
  }

  function row(p: AdminProfile): HTMLElement {
    const edit = button('Edytuj', () => {
      editing = p.id;
      say('');
      paint();
    }, { class: 'btn ghost', testid: 'profile-edit' });
    const reset = button('Wyzeruj postęp', () => {
      if (!confirm(`Wyzerować cały postęp profilu „${p.name}” (słówka, gwiazdki, serię) na serwerze i na wszystkich urządzeniach? Tego nie da się cofnąć.`)) return;
      void act(async () => {
        const r = await client.resetProfile(p.id);
        return r.ok ? { ok: true } : { ok: false, message: r.message };
      }, `Postęp profilu ${p.name} wyzerowany.`, false);
    }, { class: 'btn link danger', testid: 'profile-reset' });
    const archive = button(p.archived ? 'Przywróć' : 'Archiwizuj', () => {
      if (!p.archived && !confirm(`Zarchiwizować profil „${p.name}”? Zniknie z wyboru na tabletach, ale postęp zostaje na serwerze — możesz go przywrócić. Zapis nagrań głosu tego profilu zostanie wyłączony, a profil przestanie być brany do kalibracji (zapisane już nagrania zostają do skasowania; kopia na GitHubie, jeśli była, zniknie przy następnym przebiegu zadania „Kalibracja”; po przywróceniu trzeba włączyć zapis od nowa).`)) return;
      void act(async () => {
        const r = await client.archiveProfile(p.id, !p.archived);
        return r.ok ? { ok: true } : { ok: false, message: r.message };
      }, p.archived ? `Przywrócono profil: ${p.name}.` : `Zarchiwizowano profil: ${p.name}.`, false, true);
    }, { class: 'btn ghost', testid: 'profile-archive' });
    return h('li', { class: `adm-row${p.archived ? ' off' : ''}`, 'data-testid': 'profile-row', 'data-profile': p.id },
      avatarEl(p, 'md'),
      h('div', { class: 'adm-main' }, h('strong', null, p.name), p.hasPin && h('span', { class: 'badge', 'data-testid': 'profile-has-pin', title: 'Ten profil ma PIN dziecka' }, '🔒 PIN'), p.archived && h('span', { class: 'badge bad' }, 'zarchiwizowany'), h('div', { class: 'muted', 'data-testid': 'profile-summary' }, profileLine(p, now()))),
      h('div', { class: 'adm-actions' }, edit, archive, reset),
      recordingBlock(p),
    );
  }

  /**
   * Tekst zgody, który rodzic akceptuje przy włączeniu zapisu (SPEC §23.40.9). Numer wersji: RECORDING_CONSENT_VERSION (src/lib/recordingConsent.ts) — zmieniając treść w sposób, który
   * poszerza to, co wolno z nagraniem zrobić (nowe miejsce, nowe narzędzie, nowa osoba), trzeba podnieść wersję, żeby stare zgody nie obejmowały nowego zakresu.
   * Każde zewnętrzne miejsce i narzędzie, do którego nagranie może trafić, jest tu wymienione z nazwy.
   */
  function consentText(name: string): string {
    return (
      `Włączyć zapisywanie nagrań profilu „${name}”?\n\n` +
      'Aplikacja będzie wysyłać na serwer nagrania głosu dziecka z zadań „powiedz zdanie” w krokach 1–2 lekcji — razem z oceną i z tym, co usłyszał Azure (zapisanym jako tekst) — żeby sprawdzić i dostroić ocenę wymowy. Obok nagrania zapisują się: obie pełne surowe odpowiedzi Azure na tę próbę (ocena wymowy i zwykłe rozpoznawanie), ustawienia rozpoznawania, wersja aplikacji, progi oceny oraz opis przeglądarki i systemu urządzenia (User-Agent, np. „iPad; iOS 17”). Nagranie to wszystko, co usłyszał mikrofon, więc mogą być w nim też dźwięki z pokoju.\n\n' +
      'Gdzie trafiają nagrania:\n' +
      '• Cloudflare (prywatny bucket Twojego konta, bez publicznych adresów) — tu zostają na stałe i nie wygasają same.\n' +
      '• Zadaniem „Kalibracja” (uruchamianym ręcznie, tylko z konta właściciela repozytorium) mogą być pobrane do prywatnego repozytorium GitHub tej aplikacji: najpierw jako plik tymczasowy zadania (do doby), a potem wraz z wynikami na osobnej gałęzi repozytorium, dopóki następny przebieg zadania jej nie nadpisze. Przetwarzają je tam, na serwerze GitHuba, programy Whisper (OpenAI) i wav2vec2 (Meta) — bez wysyłania nagrań do OpenAI ani Meta.\n' +
      '• Wyniki i nagrania w tym repozytorium ogląda deweloper aplikacji — asystent Claude (Anthropic), z którym ją tworzysz; treść, którą otworzy w rozmowie, trafia do Anthropic.\n\n' +
      'Kalibracja bierze tylko nagrania profili, które mają zgodę na TEN tekst: wyłączenie zapisu, archiwizacja profilu albo zmiana tekstu zgody wyklucza profil z następnego przebiegu. To nie usuwa od razu tego, co już trafiło na GitHub — znika przy następnym przebiegu zadania, a GitHub może jeszcze przez jakiś czas przechowywać starsze wersje.\n\n' +
      'Nie słuchasz ich Ty ani nikt inny z aplikacji. Skasujesz je przyciskiem „Usuń nagrania”. Wyłączenie przełącznika nie kasuje już zapisanych nagrań — po ponownym włączeniu (na ten sam tekst zgody) wrócą do kalibracji, chyba że wcześniej je skasujesz.'
    );
  }

  /** Nagrania głosu do strojenia oceny (SPEC §23.39): zgoda rodzica (domyślnie wyłączona), podsumowanie i kasowanie. Dziecko nie ma tu wejścia. */
  function recordingBlock(p: AdminProfile): HTMLElement {
    const available = data?.recordingsAvailable === true;
    const on = p.recordVoice;
    // włączyć można tylko, gdy serwer ma gdzie zapisywać; wyłączyć — zawsze
    const blocked = !on && !available;
    const sw = button(on ? '● włączone' : '○ wyłączone', () => {
      if (!on && !confirm(consentText(p.name))) return;
      void act(async () => {
        const r = await client.setRecording(p.id, !on);
        return r.ok ? { ok: true } : { ok: false, message: r.message };
      }, on ? `Zapis nagrań profilu ${p.name} wyłączony. Nagrania, które już są, zostają do skasowania; z kalibracji na GitHubie znikną przy następnym przebiegu zadania „Kalibracja”.` : `Zapis nagrań profilu ${p.name} włączony.`, false, true);
    }, { class: `btn small ${on ? 'on' : 'ghost'}`, testid: 'profile-recording-toggle', disabled: blocked, title: blocked ? 'Serwer nie ma jeszcze miejsca na nagrania' : '' });
    sw.setAttribute('role', 'switch');
    sw.setAttribute('aria-checked', on ? 'true' : 'false');
    // przycisk jest także przy zerze policzonych nagrań: po awarii mógł zostać plik, o którym baza nie wie, a serwer sprząta i takie (lista po przedrostku)
    const wipe =
      available || p.recordings.count > 0
        ? button('Usuń nagrania', () => {
            if (!confirm(`Usunąć wszystkie nagrania profilu „${p.name}”${p.recordings.count > 0 ? ` (${p.recordings.count})` : ''} z serwera, razem z zapisanymi odpowiedziami Azure? Tego nie da się cofnąć. Jeśli były pobrane do kalibracji na GitHubie, znikną stamtąd przy następnym przebiegu zadania „Kalibracja” (GitHub może jeszcze przez jakiś czas przechowywać starsze wersje).`)) return;
            void act(async () => removeRecordings(p.id), `Nagrania profilu ${p.name} usunięte.`, false);
          }, { class: 'btn link danger', testid: 'profile-recordings-delete' })
        : null;
    // zgoda udzielona pod starszym tekstem (SPEC §23.40.9): nagrania zostają w Cloudflare, ale nie są używane do kalibracji na GitHubie, dopóki rodzic nie zaakceptuje nowego tekstu
    const stale = on && (p.recordConsentV ?? 1) < RECORDING_CONSENT_VERSION;
    const renew = stale
      ? button('Zaakceptuj nowy tekst zgody', () => {
          if (!confirm(consentText(p.name))) return;
          void act(async () => {
            const r = await client.setRecording(p.id, true);
            return r.ok ? { ok: true } : { ok: false, message: r.message };
          }, `Zgoda profilu ${p.name} odnowiona.`, false, true);
        }, { class: 'btn small', testid: 'profile-recording-renew' })
      : null;
    return h('div', { class: 'adm-rec', 'data-testid': 'profile-recording' },
      h('span', { class: 'adm-rec-label' }, 'Zapisuj nagrania do strojenia'),
      sw,
      h('span', { class: 'muted', 'data-testid': 'profile-recording-summary' }, recordingLine(p, now())),
      renew,
      wipe,
      stale && h('p', { class: 'muted adm-rec-note', 'data-testid': 'profile-recording-stale' }, 'Ta zgoda dotyczy starszego tekstu: nagrania zostają w Cloudflare, ale nie są pobierane do kalibracji na GitHubie. Zaakceptuj nowy tekst, jeśli się z nim zgadzasz.'),
      blocked && h('p', { class: 'muted adm-rec-note', 'data-testid': 'profile-recording-unavailable' }, 'Serwer nie ma jeszcze prywatnego miejsca na nagrania (bucket „naative-nagrania”) — przełącznik zadziała po jego podłączeniu.'),
    );
  }

  /** Nagrania profili, które zniknęły z bazy (twarde usunięcie SQL-em): same pliki i wpisy bez imienia, więc tylko liczba, rozmiar i przycisk. */
  function orphanBlock(orphans: Array<{ id: string; count: number; bytes: number }>): HTMLElement {
    return h('div', { class: 'card adm-orphans', 'data-testid': 'orphan-recordings' },
      h('strong', null, 'Nagrania usuniętych profili'),
      h('p', { class: 'muted' }, 'Tych profili nie ma już w bazie (zostały usunięte ręcznie), ale ich nagrania głosu zostały na serwerze. Możesz je skasować.'),
      ...orphans.map((o) =>
        h('div', { class: 'row', 'data-testid': 'orphan-recording', 'data-profile': o.id },
          h('span', { class: 'muted' }, `${o.count} ${plPlural(o.count, 'nagranie', 'nagrania', 'nagrań')} · ${formatBytes(o.bytes)}`),
          button('Usuń nagrania', () => {
            if (!confirm(`Usunąć wszystkie nagrania usuniętego profilu (${o.count}) z serwera, razem z zapisanymi odpowiedziami Azure? Tego nie da się cofnąć. Jeśli były pobrane do kalibracji na GitHubie, znikną stamtąd przy następnym przebiegu zadania „Kalibracja” (GitHub może jeszcze przez jakiś czas przechowywać starsze wersje).`)) return;
            void act(async () => removeRecordings(o.id), 'Nagrania usuniętego profilu skasowane.', false);
          }, { class: 'btn link danger', testid: 'orphan-recordings-delete' }),
        ),
      ),
    );
  }

  /** Kasuje nagrania profilu partiami (serwer robi do kilkudziesięciu naraz), aż `remaining` = 0. */
  async function removeRecordings(id: string): Promise<{ ok: true } | { ok: false; message: string }> {
    for (let round = 0; round < 250; round++) {
      const r = await client.deleteRecordings(id);
      if (!r.ok) return { ok: false, message: r.message };
      if (r.data.remaining === 0) return { ok: true };
      if (r.data.deleted === 0) return { ok: false, message: 'Serwer nie skasował reszty nagrań — spróbuj jeszcze raz.' };
    }
    return { ok: false, message: 'Nagrań jest bardzo dużo — kliknij „Usuń nagrania” jeszcze raz.' };
  }

  function paint(): void {
    if (!data) return;
    const { profiles, limit } = data;
    const active = profiles.filter((p) => !p.archived);
    const kids: Child[] = [];
    if (profiles.length === 0) kids.push(h('p', { class: 'muted' }, 'Nie ma jeszcze żadnego profilu dziecka.'));
    else {
      kids.push(h('ul', { class: 'adm-list', 'data-testid': 'profile-list' }, ...profiles.map((p) => (editing === p.id ? h('li', null, form(p)) : row(p)))));
    }
    const orphans = data.orphanRecordings ?? [];
    if (orphans.length > 0) kids.push(orphanBlock(orphans));
    if (editing === 'new') kids.push(form(null));
    else if (active.length >= limit) kids.push(h('p', { class: 'muted' }, `Jest ${active.length} z ${limit} możliwych profili — zarchiwizuj któryś, żeby dodać nowy.`));
    else
      kids.push(button('+ Dodaj profil', () => {
        editing = 'new';
        say('');
        paint();
      }, { class: 'btn primary', testid: 'profile-new' }));
    body.replaceChildren(...(kids.filter((k): k is Node | string => k !== null && k !== undefined && k !== false && k !== 0)));
  }

  box.append(h('h2', null, 'Profile dzieci'), h('p', { class: 'muted' }, 'Każde dziecko ma własny postęp zapisany na serwerze. Na tablecie dziecko wybiera swój profil na ekranie „Kto dziś się uczy?” (z PIN-em, jeśli go ustawisz).'), note, body);
  void load();
  return box;
}
